import React from 'react';
import { Dispatch, UnknownAction } from '@reduxjs/toolkit';

import { BaseModal, Button, CallbackErrorAxios, CallbackWarningAxios, DownloadIcon, UUID } from '@umanforge/common';

import { ImportError } from 'interfaces/api_import.interfaces';
import { useImport } from '../hooks/import.hook';
import { ImportErrorComponent } from './error_import.component';
import { Select } from 'components/inputs/select.component';
import { useTranslation } from 'react-i18next';
import { ClientsIcon } from 'components/icons/clients_icon.component';
import SendFileInput from '@common_components/inputs/sendFileInput.component';
import { Loading } from 'components/loading.component';
import { BodyConfirmModal } from 'components/modals/body/body_confirm_modal.component';


interface ImportModalProps {
  title: string;
  isOpen: boolean;
  importFunction: ( clientId: string, file: File, hasHeader: boolean | undefined, token: string | null, dispatch: Dispatch<UnknownAction>, onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios ) => Promise<UUID[] | ImportError | null>;
  onClose: ( isImported?: boolean ) => void;
}

export const ImportModal: React.FC<ImportModalProps> = ( { title, isOpen, importFunction, onClose } ) => {

  /*---------------------------- Hooks ------------------------------*/

  const { t } = useTranslation();
  const {
    canImport,
    allowedClients,
    isLoading,
    selectedClient, setSelectedClient,
    selectedFile, setSelectedFile,
    tryImportData,
    isOpenConfirmHeader, closeConfirmHeader,
    importError,
    handleClose
  } = useImport( isOpen, importFunction, onClose );


  /*---------------------------- Front ------------------------------*/
  return (
    <>
      <BaseModal
        title={ title }
        isOpen={ isOpen }
        onClose={ handleClose }
        iconColor="#ef4444"
        scrollable={ true }
      >
        <>
          {
            isLoading && (
              <div className='absolute -m-4 inset-0 flex items-center justify-center bg-gray-300/50 z-[100]'>
                <div className='relative flex items-center justify-center w-[175px] h-[175px] p-3 bg-white rounded-full'>
                  <Loading/>
                </div>
              </div>
            )
          }
          <div className='flex flex-col m-8 gap-4'>
            {
              allowedClients.length > 1 ?
                <Select
                  required
                  disabled={ allowedClients.length === 1 }
                  value={ selectedClient }
                  options={ allowedClients }
                  label={ t( 'Client' ) }
                  placeholder={ t( 'No associated clients' ) }
                  icon={ <ClientsIcon className='w-7 h-7' /> }
                  onChange={ option => { setSelectedClient( option ); } }
                />
                :
                null
            }
            { /* // INFO Retirer 'text/csv' si les CSV ne fonctionne pas */ }
            <SendFileInput
              label={ t( 'common:import_file' ) }
              acceptFolders={ false }
              acceptedFormats={ [ 'text/csv', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ] }
              soloFile={ true }
              multipleFiles={ false }
              onFilesChange={ files => {
                const file = files && files.length > 0 ? files[ 0 ].file : null;
                setSelectedFile( file );
              } }
            />

            <ImportErrorComponent error={ importError } />

            <div className='flex'>
              <Button
                disable={ !canImport || importError != null }
                label={ t( 'common:import' ) }
                className={ `w-full m-1 ${ canImport ? '' : 'bg-[#0BB173] hover:bg-[#088D5C] hover:ring-1 hover:ring-[#088D5C]' } text-sm font-medium text-white`}
                icon={ <DownloadIcon className='w-[24px] h-[24px] p-0.5 text-white'/> }
                onClick={ () => {
                  if ( selectedClient != null && selectedFile != null ) {
                    tryImportData( selectedClient.object, selectedFile );
                  }
                } }
              />
            </div>
          </div>
        </>
      </BaseModal>

      <BaseModal
        id={ 'confirm_nav_modal' }
        title={ t( 'translation:need_know_file_structure' ) }
        isOpen={ isOpenConfirmHeader }
        onClose={ closeConfirmHeader }
        hasCross={ false }
        blockScroll={ false }
      >
        <BodyConfirmModal
          confirmColor='#E5E7EB'
          confirmColorHover='#D1D5DB'
          cancelColor='#E5E7EB'
          cancelColorHover='#D1D5DB'
          onCancel={ async () => {
            if ( selectedClient != null && selectedFile != null ) {
              closeConfirmHeader();
              await tryImportData( selectedClient.object, selectedFile, false );
            }
          } }
          onConfirm={ async () => {
            if ( selectedClient != null && selectedFile != null ) {
              closeConfirmHeader();
              await tryImportData( selectedClient.object, selectedFile, true );
            }
          } }
          cancelText={ t( 'common:no' ) }
          confirmText={ t( 'common:yes' ) }
        >
          <span className='flex flex-col w-full h-full p-3 my-6 text-[16px] font-[475] text-gray-800'>
            { t( 'translation:first_row_contain_header' ) }
          </span>
        </BodyConfirmModal>
      </BaseModal>
    </>
  );

};